import type React from 'react';
import { useState, useRef, useCallback, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import { toast } from 'sonner';
import {
  Bot,
  MessageSquare,
  Globe,
  CheckSquare,
  Brain,
  Zap,
  Bell,
  Activity,
  Settings,
  X,
  Save,
  Trash2,
  Eye,
  EyeOff,
  Circle,
  CheckCircle2,
  Clock,
  ListTodo,
  Key,
  Github,
  Link,
  Play,
  Pause,
  Check,
  Ban,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  Calendar,
  Pencil,
  AlertTriangle,
  Eye,
  EyeOff,
  Copy,
  Check,
  Info,
} from 'lucide-react';
import {
  useTaskingBotChannels,
  useSaveChannel,
  useDeleteChannel,
  CHANNEL_META,
  type ChannelConfig,
} from '../helpers/useTaskingBotChannels';
import {
  useTaskingBotTasks,
  useUpdateTask,
  useDeleteTask,
  type TaskingBotTask,
} from '../helpers/useTaskingBotTasks';
import type { TaskingBotChannelType } from '../helpers/schema';
import { WorkflowListPanel } from './WorkflowListPanel';
import styles from './TaskingBotDashboard.module.css';

// Normalize natural-language date strings to YYYY-MM-DD for <input type="date">
function toDateValue(v: string | null | undefined): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const lower = v.trim().toLowerCase();
  const now = new Date();
  if (lower === 'today' || lower === 'now') return now.toISOString().slice(0, 10);
  if (lower === 'tomorrow') { const d = new Date(now); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
  if (lower === 'yesterday') { const d = new Date(now); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }
  const parsed = new Date(v.trim());
  if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 2000) return parsed.toISOString().slice(0, 10);
  return '';
}

// ─── Integration definitions ──────────────────────────────────────────────

interface IntegrationField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'email' | 'url';
  placeholder: string;
  required?: boolean;
}

interface IntegrationMeta {
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  description: string;
  fields: IntegrationField[];
}

interface CustomIntegrationRecord {
  id?: string;
  name: string;
  label: string;
  description?: string | null;
  fields: Array<{
    key: string;
    label: string;
    type: 'text' | 'password' | 'url';
    placeholder?: string;
    required?: boolean;
  }>;
}

// ─── Manual Task Execution for Testing ────────────────────────────────────
async function executeTaskNow(taskId: string) {
  try {
    const executeEndpoints = [
      '/_api/taskingbot/tasks-execute-now',
      '/_api/taskingbot/tasks/execute',
    ];

    for (const url of executeEndpoints) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ taskId }),
      });

      if (res.status === 404) {
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim().toLowerCase();
      if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
        toast.error('Something went wrong starting the task. Please try again.');
        return;
      }

      let data: any = null;
      try {
        data = text ? superjson.parse(text) : null;
      } catch {
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          const objectStart = text.indexOf('{');
          const objectEnd = text.lastIndexOf('}');
          if (objectStart >= 0 && objectEnd > objectStart) {
            const candidate = text.slice(objectStart, objectEnd + 1);
            try {
              data = superjson.parse(candidate);
            } catch {
              data = JSON.parse(candidate);
            }
          }
        }
      }

      if (res.ok) {
        toast.success('Task execution started!');
        return data;
      } else {
        toast.error(data?.error || 'Execution failed');
        return;
      }
    }

    toast.error('Task execution is temporarily unavailable. Please try again later.');
  } catch (err) {
    toast.error('Error executing task');
  }
}

// ─── Subtask Management ────────────────────────────────────────────────────
interface Subtask {
  id: string;
  title: string;
  completed: boolean;
  order: number;
}

function parseSubtasks(metadata: string | null): Subtask[] {
  if (!metadata) return [];
  try {
    const parsed = JSON.parse(metadata);
    return Array.isArray(parsed.subtasks) ? parsed.subtasks : [];
  } catch {
    return [];
  }
}

function updateSubtasks(metadata: string | null, subtasks: Subtask[]): string {
  try {
    const parsed = metadata ? JSON.parse(metadata) : {};
    parsed.subtasks = subtasks;
    return JSON.stringify(parsed);
  } catch {
    return JSON.stringify({ subtasks });
  }
}

function getSubtaskProgress(subtasks: Subtask[]): { completed: number; total: number } {
  return {
    completed: subtasks.filter((s) => s.completed).length,
    total: subtasks.length,
  };
}

const INTEGRATION_META: Record<string, IntegrationMeta> = {
  github: {
    label: 'GitHub',
    icon: Github,
    description: 'Create issues, PRs, search code',
    fields: [
      {
        key: 'token',
        label: 'Personal Access Token',
        type: 'password',
        placeholder: 'ghp_...',
        required: true,
      },
    ],
  },
  // clawdhub and openclaw use global server keys (CLHUB_API_KEY, GITHUB_TOKEN), not per-user
  slack: {
    label: 'Slack',
    icon: MessageSquare,
    description: 'Post messages, search channels',
    fields: [
      {
        key: 'bot_token',
        label: 'Bot Token',
        type: 'password',
        placeholder: 'xoxb-...',
        required: true,
      },
    ],
  },
  kalshi: {
    label: 'Kalshi',
    icon: Zap,
    description: 'Prediction market trading',
    fields: [
      {
        key: 'accessKey',
        label: 'Access Key',
        type: 'password',
        placeholder: 'Your Kalshi API key',
        required: true,
      },
    ],
  },
  notion: {
    label: 'Notion',
    icon: Brain,
    description: 'Read and write Notion pages/databases',
    fields: [
      {
        key: 'api_key',
        label: 'Integration Token',
        type: 'password',
        placeholder: 'secret_...',
        required: true,
      },
      { key: 'database_id', label: 'Database ID', type: 'text', placeholder: '...optional...' },
    ],
  },
  trello: {
    label: 'Trello',
    icon: CheckSquare,
    description: 'Manage Trello boards and cards',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'text',
        placeholder: 'Your Trello API key',
        required: true,
      },
      {
        key: 'token',
        label: 'Token',
        type: 'password',
        placeholder: 'Your Trello token',
        required: true,
      },
    ],
  },
  discord: {
    label: 'Discord',
    icon: Bell,
    description: 'Send messages via Discord webhook',
    fields: [
      {
        key: 'webhook_url',
        label: 'Webhook URL',
        type: 'password',
        placeholder: 'https://discord.com/api/webhooks/...',
        required: true,
      },
    ],
  },
};

const ALL_INTEGRATIONS = Object.keys(INTEGRATION_META);

function useCredentials() {
  return useQuery({
    queryKey: ['taskingbot', 'credentials', 'masked'],
    queryFn: async () => {
      const res = await fetch('/_api/taskingbot/credentials', { credentials: 'include' });
      if (!res.ok) return { credentials: {} };
      const data = await res.json() as any;
      // Server returns { credentials: { name: { field: maskedValue } } }
      return { credentials: data.credentials || data.integrations || {} };
    },
  });
}

function useSaveCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      integration,
      config,
    }: {
      integration: string;
      config: Record<string, string>;
    }) => {
      const res = await fetch('/_api/taskingbot/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ integration, config }),
      });
      if (!res.ok) {
        const text = await res.text();
        let errorMsg = 'Failed to save';
        try {
          const parsed = superjson.parse<{ error?: string }>(text);
          errorMsg = parsed?.error || errorMsg;
        } catch {
          try {
            const parsed = JSON.parse(text) as { error?: string };
            errorMsg = parsed?.error || text || errorMsg;
          } catch {
            errorMsg = text || errorMsg;
          }
        }
        throw new Error(errorMsg);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taskingbot', 'credentials'] }),
  });
}

function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (integration: string) => {
      const res = await fetch('/_api/taskingbot/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ integration, delete: true }),
      });
      if (!res.ok) {
        const text = await res.text();
        let errorMsg = 'Failed to delete';
        try {
          errorMsg = JSON.parse(text)?.error || errorMsg;
        } catch {
          errorMsg = text || errorMsg;
        }
        throw new Error(errorMsg);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taskingbot', 'credentials'] }),
  });
}

function useCustomIntegrations() {
  return useQuery({
    queryKey: ['taskingbot', 'custom-integrations'],
    queryFn: async () => {
      const res = await fetch('/_api/taskingbot/custom-integrations', { credentials: 'include' });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to load custom integrations');
      }
      const text = await res.text();
      try {
        return superjson.parse<{ integrations: CustomIntegrationRecord[] }>(text);
      } catch {
        return JSON.parse(text) as { integrations: CustomIntegrationRecord[] };
      }
    },
  });
}

function useSaveCustomIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      label: string;
      description?: string;
      fields: CustomIntegrationRecord['fields'];
    }) => {
      const res = await fetch('/_api/taskingbot/custom-integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        let errorMsg = 'Failed to save custom integration';
        try {
          const parsed = superjson.parse<{ error?: string }>(text);
          errorMsg = parsed?.error || errorMsg;
        } catch {
          try {
            const parsed = JSON.parse(text) as { error?: string };
            errorMsg = parsed?.error || text || errorMsg;
          } catch {
            errorMsg = text || errorMsg;
          }
        }
        throw new Error(errorMsg);
      }
      const text = await res.text();
      try {
        return superjson.parse<{ success: boolean; integration: CustomIntegrationRecord }>(text);
      } catch {
        return JSON.parse(text) as { success: boolean; integration: CustomIntegrationRecord };
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taskingbot', 'custom-integrations'] });
    },
  });
}

// ─── Simple Credentials Panel ────────────────────────────────────────────

function SimpleCredentialsPanel({
  credData,
}: {
  credData?: { integrations: Record<string, Record<string, string>> };
}) {
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const save = useSaveCredential();
  const del = useDeleteCredential();

  // Flatten all credentials into a simple list of { integration, key, masked }
  const allSecrets: { integration: string; key: string; masked: string }[] = [];
  const credMap = credData?.credentials || credData?.integrations || {};
  if (credMap && Object.keys(credMap).length > 0) {
    for (const [integration, fields] of Object.entries(credMap)) {
      for (const [key, masked] of Object.entries(fields)) {
        allSecrets.push({ integration, key, masked });
      }
    }
  }
  allSecrets.sort((a, b) => {
    const aLabel = a.integration === '_custom_' ? a.key : `${a.integration}/${a.key}`;
    const bLabel = b.integration === '_custom_' ? b.key : `${b.integration}/${b.key}`;
    return aLabel.localeCompare(bLabel);
  });

  const handleSave = () => {
    const trimmedName = name.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const trimmedValue = value.trim();
    if (!trimmedName || !trimmedValue) {
      toast.error('Name and value are required');
      return;
    }
    save.mutate(
      { integration: '_custom_', config: { [trimmedName]: trimmedValue } },
      {
        onSuccess: () => {
          toast.success(`Secret "${trimmedName}" saved`);
          setName('');
          setValue('');
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save'),
      },
    );
  };

  const handleDelete = (integration: string, key: string) => {
    if (integration === '_custom_') {
      // For custom secrets, save the integration with the key removed
      // We need to delete the whole integration if it's the only key
      del.mutate(integration, {
        onSuccess: () => toast.success(`Deleted "${key}"`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete'),
      });
    } else {
      del.mutate(integration, {
        onSuccess: () => toast.success(`Deleted "${integration}" credentials`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete'),
      });
    }
  };

  return (
    <div className={styles.credPanel}>
      <div className={styles.credAddForm}>
        <div className={styles.credAddRow}>
          <div className={styles.credField}>
            <label className={styles.credFieldLabel}>Name</label>
            <input
              className={styles.credInput}
              type="text"
              placeholder="API_KEY"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div className={styles.credField} style={{ flex: 1 }}>
            <label className={styles.credFieldLabel}>Value</label>
            <div className={styles.credValueWrap}>
              <input
                className={styles.credInput}
                type={showValue ? 'text' : 'password'}
                placeholder="sk-..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <button
                type="button"
                className={styles.credEyeBtn}
                onClick={() => setShowValue(!showValue)}
                title={showValue ? 'Hide' : 'Show'}
              >
                {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <button
            type="button"
            className={styles.credSaveBtn}
            onClick={handleSave}
            disabled={save.isPending}
          >
            <Save size={14} />
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {allSecrets.length > 0 && (
        <StoredCredentialsList secrets={allSecrets} onDelete={handleDelete} />
      )}

      {allSecrets.length === 0 && (
        <div className={styles.settingsEmpty}>
          No secrets saved yet. Add your first API key above.
        </div>
      )}
    </div>
  );
}

// ─── Stored Credentials List with edit/copy/reveal ────────────────────────

function StoredCredentialsList({
  secrets,
  onDelete,
}: {
  secrets: { integration: string; key: string; masked: string }[];
  onDelete: (integration: string, key: string) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const save = useSaveCredential();

  const handleEdit = (integration: string, key: string) => {
    setEditing(`${integration}.${key}`);
    setEditValue('');
  };

  const handleSaveEdit = async (integration: string, key: string) => {
    if (!editValue.trim()) { setEditing(null); return; }
    try {
      await save.mutateAsync({ integration, config: { [key]: editValue.trim() } });
      setEditing(null);
      setEditValue('');
    } catch {}
  };

  const handleCopy = async (masked: string, id: string) => {
    await navigator.clipboard.writeText(masked);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className={styles.credList}>
      {secrets.map(({ integration, key, masked }) => {
        const id = `${integration}.${key}`;
        const displayName = integration === '_custom_' ? key : `${integration.toUpperCase()} / ${key}`;
        const isEditing = editing === id;

        return (
          <div key={id} className={styles.credRow}>
            <div className={styles.credRowInfo}>
              <Key size={13} className={styles.credRowIcon} />
              <span className={styles.credRowName}>{displayName}</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(integration, key); if (e.key === 'Escape') setEditing(null); }}
                  placeholder="Enter new value..."
                  autoFocus
                  style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', color: 'var(--foreground)', fontSize: '0.8rem', minWidth: 0 }}
                />
              ) : (
                <span className={styles.credRowMasked}>{masked || '••••••••'}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
              {isEditing ? (
                <button type="button" className={styles.credDeleteBtn} onClick={() => handleSaveEdit(integration, key)} title="Save" style={{ color: 'var(--success, #22c55e)' }}>
                  <Check size={14} />
                </button>
              ) : (
                <>
                  <button type="button" className={styles.credDeleteBtn} onClick={() => handleCopy(masked, id)} title="Copy masked value">
                    {copied === id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <button type="button" className={styles.credDeleteBtn} onClick={() => handleEdit(integration, key)} title="Edit">
                    <Pencil size={14} />
                  </button>
                </>
              )}
              <button type="button" className={styles.credDeleteBtn} onClick={() => onDelete(integration, key)} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── IntegrationForm ──────────────────────────────────────────────────────

function IntegrationForm({
  integrationKey,
  meta,
  masked,
  onClose,
}: {
  integrationKey: string;
  meta: IntegrationMeta;
  masked?: Record<string, string>;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [extraFields, setExtraFields] = useState<{ key: string; value: string }[]>([]);
  const save = useSaveCredential();
  const del = useDeleteCredential();

  // Detect extra keys from masked data that aren't in meta.fields
  const predefinedKeys = new Set(meta.fields.map((f) => f.key));
  const existingExtraKeys = masked ? Object.keys(masked).filter((k) => !predefinedKeys.has(k)) : [];

  const toUpperSnake = (s: string) =>
    s
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');

  const handleSave = () => {
    const missingRequired = meta.fields.filter(
      (f) => f.required && !values[f.key]?.trim() && !masked?.[f.key],
    );
    if (missingRequired.length > 0) {
      toast.error(`Missing required: ${missingRequired.map((f) => f.label).join(', ')}`);
      return;
    }

    const config: Record<string, string> = {};
    for (const f of meta.fields) {
      const nextValue = values[f.key]?.trim();
      if (nextValue) config[f.key] = nextValue;
    }
    // Include updated existing extra keys
    for (const ek of existingExtraKeys) {
      const nextValue = values[ek]?.trim();
      if (nextValue) config[ek] = nextValue;
    }
    // Include additional fields (UPPER_CASE keys)
    for (const ef of extraFields) {
      const key = toUpperSnake(ef.key);
      const val = ef.value.trim();
      if (key && val) config[key] = val;
    }
    save.mutate(
      { integration: integrationKey, config },
      {
        onSuccess: () => {
          toast.success(`${meta.label} saved`);
          onClose();
        },
        onError: (err) =>
          toast.error(
            `Failed to save ${meta.label}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          ),
      },
    );
  };

  const handleDisconnect = () => {
    del.mutate(integrationKey, {
      onSuccess: () => {
        toast.success(`${meta.label} disconnected`);
        onClose();
      },
    });
  };

  return (
    <div className={styles.configForm}>
      <div className={styles.configHeader}>
        <span className={styles.configTitle}>Configure {meta.label}</span>
        <button className={styles.configClose} onClick={onClose}>
          <X size={14} />
        </button>
      </div>
      <div className={styles.configBody}>
        <p className={styles.configNote}>{meta.description}</p>
        {meta.fields.map((field) => (
          <div key={field.key} className={styles.configField}>
            <label className={styles.configLabel}>
              {field.label}
              {field.required && <span style={{ color: 'var(--color-error, #e53e3e)' }}> *</span>}
            </label>
            {masked?.[field.key] && !values[field.key] && (
              <div className={styles.configMasked}>
                Current: <code>{masked[field.key]}</code>
              </div>
            )}
            <div className={styles.configInputWrapper}>
              <input
                className={styles.configInput}
                type={
                  field.type === 'password' && !showPasswords[field.key]
                    ? 'password'
                    : field.type === 'email'
                      ? 'email'
                      : field.type === 'url'
                        ? 'url'
                        : 'text'
                }
                value={values[field.key] || ''}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                placeholder={
                  masked?.[field.key] ? 'Leave blank to keep current' : field.placeholder
                }
              />
              {field.type === 'password' && (
                <button
                  className={styles.configTogglePassword}
                  onClick={() =>
                    setShowPasswords({ ...showPasswords, [field.key]: !showPasswords[field.key] })
                  }
                >
                  {showPasswords[field.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Existing extra keys from DB */}
        {existingExtraKeys.length > 0 && (
          <div className={styles.configField} style={{ marginTop: 12 }}>
            <label className={styles.configLabel} style={{ opacity: 0.6, fontSize: '0.72rem' }}>
              Additional Values
            </label>
            {existingExtraKeys.map((ek) => (
              <div key={ek} style={{ marginBottom: 6 }}>
                <label className={styles.configLabel} style={{ fontSize: '0.72rem' }}>
                  {ek}
                </label>
                <div className={styles.configMasked}>
                  Current: <code>{masked![ek]}</code>
                </div>
                <input
                  className={styles.configInput}
                  type="password"
                  value={values[ek] || ''}
                  onChange={(e) => setValues({ ...values, [ek]: e.target.value })}
                  placeholder="Leave blank to keep current"
                />
              </div>
            ))}
          </div>
        )}

        {/* Add new extra fields */}
        {extraFields.map((ef, idx) => (
          <div key={idx} className={styles.configField}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className={styles.configInput}
                type="text"
                value={ef.key}
                onChange={(e) => {
                  const next = [...extraFields];
                  next[idx] = { ...next[idx], key: toUpperSnake(e.target.value) };
                  setExtraFields(next);
                }}
                placeholder="KEY_NAME"
                style={{ flex: '0 0 40%', fontFamily: 'monospace', textTransform: 'uppercase' }}
              />
              <input
                className={styles.configInput}
                type="password"
                value={ef.value}
                onChange={(e) => {
                  const next = [...extraFields];
                  next[idx] = { ...next[idx], value: e.target.value };
                  setExtraFields(next);
                }}
                placeholder="Value"
                style={{ flex: 1 }}
              />
              <button
                className={styles.configDeleteBtn}
                style={{ padding: '6px 8px', minWidth: 'auto' }}
                onClick={() => setExtraFields((prev) => prev.filter((_, i) => i !== idx))}
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setExtraFields((prev) => [...prev, { key: '', value: '' }])}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 8,
            padding: '6px 10px',
            fontSize: '0.74rem',
            background: 'transparent',
            border: '1px dashed color-mix(in srgb, var(--border) 60%, transparent)',
            borderRadius: 'var(--radius-sm, 8px)',
            color: 'var(--muted-foreground)',
            cursor: 'pointer',
          }}
        >
          <Plus size={12} /> Add Value
        </button>
      </div>
      <div className={styles.configActions}>
        {masked && Object.keys(masked).length > 0 && (
          <button
            className={styles.configDeleteBtn}
            onClick={handleDisconnect}
            disabled={del.isPending}
          >
            <Trash2 size={14} /> Disconnect
          </button>
        )}
        <button className={styles.configSaveBtn} onClick={handleSave} disabled={save.isPending}>
          <Save size={14} /> {save.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Channels ────────────────────────────────────────────────────────────

function toIntegrationSlug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

function CustomIntegrationBuilderForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (integrationName: string) => void;
}) {
  const saveCustom = useSaveCustomIntegration();
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<CustomIntegrationRecord['fields']>([
    {
      key: 'api_key',
      label: 'API Token',
      type: 'password',
      placeholder: 'Paste API token',
      required: true,
    },
  ]);

  const updateField = (
    index: number,
    patch: Partial<CustomIntegrationRecord['fields'][number]>,
  ) => {
    setFields((prev) => prev.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  };

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        key: `field_${prev.length + 1}`,
        label: `Field ${prev.length + 1}`,
        type: 'text',
        placeholder: '',
        required: false,
      },
    ]);
  };

  const removeField = (index: number) => {
    setFields((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSave = () => {
    const normalizedName = toIntegrationSlug(name || label);
    if (!normalizedName) return toast.error('Integration key is required');
    if (!label.trim()) return toast.error('Display name is required');

    const normalizedFields = fields
      .map((field) => ({
        ...field,
        key: toIntegrationSlug(field.key || field.label),
        label: field.label.trim(),
        placeholder: (field.placeholder || '').trim(),
      }))
      .filter((field) => field.key && field.label);

    if (normalizedFields.length === 0) return toast.error('Add at least one field');

    saveCustom.mutate(
      {
        name: normalizedName,
        label: label.trim(),
        description: description.trim() || undefined,
        fields: normalizedFields,
      },
      {
        onSuccess: ({ integration }) => {
          toast.success(`Created ${integration.label}`);
          onCreated(integration.name);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to create integration'),
      },
    );
  };

  return (
    <div className={styles.configForm}>
      <div className={styles.configHeader}>
        <span className={styles.configTitle}>New Custom Integration</span>
        <button className={styles.configClose} onClick={onClose}>
          <X size={14} />
        </button>
      </div>
      <div className={styles.configBody}>
        <p className={styles.configNote}>
          Create a reusable credential schema. TaskingBot will check these credentials before using
          related skills/workflows.
        </p>

        <div className={styles.configField}>
          <label className={styles.configLabel}>Display Name</label>
          <input
            className={styles.configInput}
            type="text"
            value={label}
            onChange={(e) => {
              const next = e.target.value;
              setLabel(next);
              if (!name.trim()) setName(toIntegrationSlug(next));
            }}
            placeholder="Acme CRM"
          />
        </div>

        <div className={styles.configField}>
          <label className={styles.configLabel}>Integration Key</label>
          <input
            className={styles.configInput}
            type="text"
            value={name}
            onChange={(e) => setName(toIntegrationSlug(e.target.value))}
            placeholder="acme_crm"
          />
        </div>

        <div className={styles.configField}>
          <label className={styles.configLabel}>Description</label>
          <input
            className={styles.configInput}
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Search contacts, create deals, sync notes"
          />
        </div>

        <div className={styles.integrationBuilderFields}>
          <div className={styles.integrationBuilderHeader}>
            <span className={styles.configLabel}>Credential Fields</span>
            <button className={styles.integrationBuilderBtn} onClick={addField}>
              <Plus size={12} /> Add field
            </button>
          </div>

          {fields.map((field, index) => (
            <div key={`${field.key}-${index}`} className={styles.integrationBuilderRow}>
              <div className={styles.integrationBuilderGrid}>
                <input
                  className={styles.configInput}
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(index, { label: e.target.value })}
                  placeholder="Field label"
                />
                <input
                  className={styles.configInput}
                  type="text"
                  value={field.key}
                  onChange={(e) => updateField(index, { key: toIntegrationSlug(e.target.value) })}
                  placeholder="field_key"
                />
                <select
                  className={styles.configInput}
                  value={field.type}
                  onChange={(e) =>
                    updateField(index, {
                      type: e.target.value as CustomIntegrationRecord['fields'][number]['type'],
                    })
                  }
                >
                  <option value="password">password</option>
                  <option value="text">text</option>
                  <option value="url">url</option>
                </select>
                <input
                  className={styles.configInput}
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => updateField(index, { placeholder: e.target.value })}
                  placeholder="Placeholder"
                />
              </div>
              <div className={styles.integrationBuilderActions}>
                <label className={styles.integrationBuilderCheckbox}>
                  <input
                    type="checkbox"
                    checked={Boolean(field.required)}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                  />
                  Required
                </label>
                <button
                  className={styles.integrationBuilderBtnGhost}
                  onClick={() => removeField(index)}
                  disabled={fields.length <= 1}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.configActions}>
        <button className={styles.configDeleteBtn} onClick={onClose}>
          <X size={14} /> Cancel
        </button>
        <button
          className={styles.configSaveBtn}
          onClick={handleSave}
          disabled={saveCustom.isPending}
        >
          <Save size={14} /> {saveCustom.isPending ? 'Creating...' : 'Create Integration'}
        </button>
      </div>
    </div>
  );
}

const ALL_CHANNEL_TYPES: TaskingBotChannelType[] = [
  'web',
  'telegram',
  'whatsapp',
  'signal',
  'discord',
];

const CHANNEL_ICONS: Record<
  TaskingBotChannelType,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  web: Globe,
  telegram: MessageSquare,
  whatsapp: MessageSquare,
  signal: MessageSquare,
  discord: MessageSquare,
  voice: Activity,
};

function ChannelConfigForm({
  channelType,
  existing,
  onClose,
}: {
  channelType: TaskingBotChannelType;
  existing?: ChannelConfig;
  onClose: () => void;
}) {
  const meta = CHANNEL_META[channelType];
  const saveChannel = useSaveChannel();
  const deleteChannel = useDeleteChannel();
  const [values, setValues] = useState<Record<string, string>>(existing ? { ...existing } : {});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [showHelp, setShowHelp] = useState(false);

  const handleSave = () => {
    saveChannel.mutate({ channelType, config: values }, { onSuccess: onClose });
  };

  const handleDisconnect = () => {
    deleteChannel.mutate(channelType, { onSuccess: onClose });
  };

  if (meta.fields.length === 0) {
    return (
      <div className={styles.configForm}>
        <div className={styles.configHeader}>
          <span className={styles.configTitle}>{meta.label}</span>
          <button className={styles.configClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className={styles.configBody}>
          <p className={styles.configNote}>{meta.helpText || 'Built-in and always connected.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.configForm}>
      <div className={styles.configHeader}>
        <span className={styles.configTitle}>Configure {meta.label}</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {meta.helpText && (
            <button
              type="button"
              className={styles.configClose}
              onClick={() => setShowHelp(!showHelp)}
              title="Setup instructions"
              style={{ color: showHelp ? 'var(--primary, #f97316)' : undefined }}
            >
              <Info size={14} />
            </button>
          )}
          <button className={styles.configClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
      </div>
      {showHelp && meta.helpText && (
        <div style={{ padding: '0.5rem 0.75rem', background: 'var(--surface, #1a2332)', borderBottom: '1px solid var(--border, #334155)', fontSize: '0.75rem', lineHeight: 1.6, color: 'var(--muted-foreground, #94a3b8)', whiteSpace: 'pre-line' }}>
          {meta.helpText}
        </div>
      )}
      <div className={styles.configBody}>
        {meta.fields.map((field) => (
          <div key={field.key} className={styles.configField}>
            <label className={styles.configLabel}>{field.label}</label>
            <div className={styles.configInputWrapper}>
              <input
                className={styles.configInput}
                type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                value={values[field.key] || ''}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                placeholder={field.placeholder}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                name={`tt-${channelType}-${field.key}-${Date.now()}`}
              />
              {field.type === 'password' && (
                <button
                  className={styles.configTogglePassword}
                  onClick={() =>
                    setShowPasswords({ ...showPasswords, [field.key]: !showPasswords[field.key] })
                  }
                >
                  {showPasswords[field.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.configActions}>
        {existing && (
          <button
            className={styles.configDeleteBtn}
            onClick={handleDisconnect}
            disabled={deleteChannel.isPending}
          >
            <Trash2 size={14} /> Disconnect
          </button>
        )}
        <button
          className={styles.configSaveBtn}
          onClick={handleSave}
          disabled={saveChannel.isPending}
        >
          <Save size={14} /> {saveChannel.isPending ? 'Saving...' : 'Save & Connect'}
        </button>
      </div>
    </div>
  );
}

// ─── Skills list ─────────────────────────────────────────────────────────

const SKILL_CATEGORIES = [
  {
    name: 'Core',
    skills: ['create_task', 'update_task', 'list_tasks', 'create_project', 'close_task'],
  },
  { name: 'Workflows', skills: ['run_workflow', 'job_search', 'daily_brief', 'inbox_triage'] },
  { name: 'Calendar', skills: ['create_event', 'list_agenda', 'block_time', 'schedule_recurring'] },
  { name: 'Memory', skills: ['save_memory', 'search_memory', 'summarize_period'] },
  { name: 'Communication', skills: ['send_email', 'http_post', 'http_get'] },
  {
    name: 'Sonos',
    skills: [
      'sonos_discover',
      'sonos_playback',
      'sonos_volume',
      'sonos_group',
      'sonos_favorites',
      'sonos_search',
    ],
  },
  {
    name: 'Philips Hue',
    skills: ['hue_get_lights', 'hue_get_rooms', 'hue_set_light', 'hue_set_scene'],
  },
  {
    name: 'Spotify',
    skills: ['spotify_play', 'spotify_pause', 'spotify_search', 'spotify_queue', 'spotify_status'],
  },
  {
    name: 'GitHub',
    skills: ['github_create_issue', 'github_list_issues', 'github_create_pr', 'github_search_code'],
  },
  { name: 'Email', skills: ['email_send', 'email_list', 'email_read'] },
  { name: 'Weather', skills: ['weather_current', 'weather_forecast'] },
  {
    name: 'Trello',
    skills: ['trello_get_boards', 'trello_get_cards', 'trello_create_card', 'trello_update_card'],
  },
  { name: 'Notion', skills: ['notion_create_page', 'notion_update_page', 'notion_query_database'] },
  { name: 'Summarize', skills: ['summarize_url'] },
  { name: 'Places', skills: ['places_search', 'places_details'] },
  { name: 'Feed', skills: ['feed_list', 'feed_create', 'feed_update', 'feed_delete'] },
];

// ─── TaskRow with inline editing ─────────────────────────────────────────

const PRIORITY_CLASSES: Record<string, string> = {
  urgent: styles.priorityUrgent,
  high: styles.priorityHigh,
  medium: styles.priorityMedium,
  low: styles.priorityLow,
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Done',
  cancelled: 'Cancelled',
};

function TaskRow({ task }: { task: TaskingBotTask }) {
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [actionError, setActionError] = useState<string | null>(null);
  const [subtasks, setSubtasks] = useState<Subtask[]>(() => parseSubtasks(task.metadata));
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const isComplete = task.status === 'completed' || task.status === 'cancelled';
  const Icon = isComplete ? CheckCircle2 : task.status === 'in_progress' ? Clock : Circle;
  const effectiveEndDate = toDateValue(task.endDate || task.dueDate);
  const effectiveEndTime = task.endTime || task.dueTime;
  const subtaskProgress = getSubtaskProgress(subtasks);

  const mutateTask = useCallback(
    (payload: Record<string, any>) => {
      setActionError(null);
      updateTask.mutate({ id: task.id, ...payload } as any, {
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Task update failed';
          setActionError(message);
          toast.error(message);
        },
      });
    },
    [task.id, updateTask],
  );

  const handleStatus = (status: TaskingBotTask['status']) => {
    mutateTask({ status });
  };

  const handleDelete = () => {
    const confirmed = window.confirm(`Delete task "${task.title}"? This cannot be undone.`);
    if (!confirmed) return;
    deleteTask.mutate(task.id);
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const t = titleDraft.trim();
    if (t && t !== task.title) {
      mutateTask({ title: t });
    } else {
      setTitleDraft(task.title);
    }
  };

  const handlePriorityChange = (priority: string) => {
    mutateTask({ priority: priority as TaskingBotTask['priority'] });
  };

  const addSubtask = () => {
    if (!newSubtaskText.trim()) return;
    const newSubtask: Subtask = {
      id: `sub_${Date.now()}`,
      title: newSubtaskText.trim(),
      completed: false,
      order: subtasks.length,
    };
    const updatedSubtasks = [...subtasks, newSubtask];
    setSubtasks(updatedSubtasks);
    setNewSubtaskText('');
    const newMetadata = updateSubtasks(task.metadata, updatedSubtasks);
    mutateTask({ metadata: newMetadata });
  };

  const toggleSubtask = (subtaskId: string) => {
    const updatedSubtasks = subtasks.map((s) =>
      s.id === subtaskId ? { ...s, completed: !s.completed } : s,
    );
    setSubtasks(updatedSubtasks);
    const newMetadata = updateSubtasks(task.metadata, updatedSubtasks);
    mutateTask({ metadata: newMetadata });
  };

  const deleteSubtask = (subtaskId: string) => {
    const updatedSubtasks = subtasks.filter((s) => s.id !== subtaskId);
    setSubtasks(updatedSubtasks);
    const newMetadata = updateSubtasks(task.metadata, updatedSubtasks);
    mutateTask({ metadata: newMetadata });
  };

  const handleStartDateChange = (startDate: string) => {
    if (startDate === (task.startDate || '')) return;
    mutateTask({ startDate: startDate || null });
  };

  const handleStartTimeChange = (startTime: string) => {
    if (startTime === (task.startTime || '')) return;
    mutateTask({ startTime: startTime || null });
  };

  const handleEndDateChange = (endDate: string) => {
    const current = task.endDate || task.dueDate || '';
    if (endDate === current) return;
    mutateTask({ endDate: endDate || null, dueDate: endDate || null });
  };

  const handleEndTimeChange = (endTime: string) => {
    const current = task.endTime || task.dueTime || '';
    if (endTime === current) return;
    mutateTask({ endTime: endTime || null, dueTime: endTime || null });
  };

  const handleCronExpressionChange = (cronExpression: string) => {
    const trimmed = cronExpression.trim();
    if (trimmed === (task.cronExpression || '')) return;
    mutateTask({ cronExpression: trimmed || null });
  };

  const handleCronTimezoneChange = (cronTimezone: string) => {
    const trimmed = cronTimezone.trim();
    if (trimmed === (task.cronTimezone || '')) return;
    mutateTask({ cronTimezone: trimmed || null });
  };

  const handleNotesChange = (notes: string) => {
    // Only fire update if value actually changed
    const currentNotes = (() => {
      try {
        const m = task.metadata ? JSON.parse(task.metadata) : {};
        return m?.notes || '';
      } catch {
        return '';
      }
    })();
    if (notes === currentNotes || notes === ((task as any).notes || '')) return;
    mutateTask({ notes });
  };

  return (
    <div className={`${styles.taskItem} ${isComplete ? styles.taskItemDone : ''}`}>
      <div className={styles.taskMain}>
        {/* Status toggle button */}
        <button
          className={`${styles.taskCheck} ${isComplete ? styles.completed : task.status === 'in_progress' ? styles.inProgress : ''}`}
          onClick={() => {
            if (task.status === 'open') handleStatus('in_progress');
            else if (task.status === 'in_progress') handleStatus('completed');
            else if (task.status === 'completed') handleStatus('open');
          }}
          title={`Status: ${STATUS_LABELS[task.status] || task.status} — click to cycle`}
        >
          <Icon size={14} />
        </button>

        <div className={styles.taskBody}>
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className={styles.taskTitleInput}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                if (e.key === 'Escape') {
                  setEditingTitle(false);
                  setTitleDraft(task.title);
                }
              }}
              autoFocus
            />
          ) : (
            <div className={styles.taskTitleRow}>
              <div
                className={`${styles.taskTitle} ${isComplete ? styles.completed : ''}`}
                onClick={() => {
                  setEditingTitle(true);
                  setTitleDraft(task.title);
                }}
                title="Click to edit title"
              >
                {task.title}
                <Pencil size={10} className={styles.editHint} />
              </div>
              {(() => {
                try {
                  const meta = task.metadata ? JSON.parse(task.metadata) : {};
                  const autonomous = meta?.autonomous || {};
                  if (!autonomous.lastRunAt) return null;
                  const isSuccess = autonomous.lastRunStatus === 'success';
                  return (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: isSuccess ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: isSuccess ? '#22c55e' : '#ef4444',
                        whiteSpace: 'nowrap',
                      }}
                      title={`Last: ${new Date(autonomous.lastRunAt).toLocaleString()}`}
                    >
                      {isSuccess ? '✓ Ran' : '✗ Failed'}
                    </span>
                  );
                } catch {
                  return null;
                }
              })()}
            </div>
          )}

          <div className={styles.taskMeta}>
            {/* Priority selector */}
            <select
              className={`${styles.taskPriority} ${PRIORITY_CLASSES[task.priority] || ''}`}
              value={task.priority}
              onChange={(e) => handlePriorityChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            >
              {['urgent', 'high', 'medium', 'low'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            {task.project && <span className={styles.taskProject}>{task.project}</span>}

            {/* Start date */}
            <div className={styles.taskDueWrapper} title="Start date">
              <Calendar size={10} style={{ opacity: 0.5, flexShrink: 0 }} />
              <input
                type="date"
                className={styles.taskDueInput}
                value={toDateValue(task.startDate)}
                onChange={(e) => handleStartDateChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Start"
              />
            </div>

            {/* Start time */}
            {task.startDate && (
              <div className={styles.taskDueWrapper} title="Start time">
                <Clock size={10} style={{ opacity: 0.5, flexShrink: 0 }} />
                <input
                  type="time"
                  className={styles.taskDueInput}
                  value={task.startTime || ''}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: '4.5rem' }}
                />
              </div>
            )}

            {/* End date */}
            <div className={styles.taskDueWrapper} title="End date">
              <Calendar size={10} style={{ opacity: 0.5, flexShrink: 0 }} />
              <input
                type="date"
                className={styles.taskDueInput}
                value={effectiveEndDate || ''}
                onChange={(e) => handleEndDateChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="End"
              />
            </div>

            {/* End time */}
            {effectiveEndDate && (
              <div className={styles.taskDueWrapper} title="End time">
                <Clock size={10} style={{ opacity: 0.5, flexShrink: 0 }} />
                <input
                  type="time"
                  className={styles.taskDueInput}
                  value={effectiveEndTime || ''}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: '4.5rem' }}
                />
              </div>
            )}

            {task.cronExpression && (
              <span className={styles.taskProject} title="Recurring schedule">
                Cron: {task.cronExpression}
              </span>
            )}
          </div>
        </div>

        <div className={styles.taskActions}>
          {(task.status === 'open' || task.status === 'in_progress') && (
            <button
              className={styles.taskActionBtn}
              title="Execute Now (Manual)"
              onClick={() => executeTaskNow(task.id)}
              style={{ color: '#f97316' }}
            >
              <Zap size={11} />
            </button>
          )}
          {task.status === 'open' && (
            <button
              className={styles.taskActionBtn}
              title="Start"
              onClick={() => handleStatus('in_progress')}
              disabled={updateTask.isPending}
            >
              <Play size={11} />
            </button>
          )}
          {task.status === 'in_progress' && (
            <button
              className={styles.taskActionBtn}
              title="Pause"
              onClick={() => handleStatus('open')}
              disabled={updateTask.isPending}
            >
              <Pause size={11} />
            </button>
          )}
          {(task.status === 'open' || task.status === 'in_progress') && (
            <button
              className={`${styles.taskActionBtn} ${styles.taskActionComplete}`}
              title="Complete"
              onClick={() => handleStatus('completed')}
              disabled={updateTask.isPending}
            >
              <Check size={11} />
            </button>
          )}
          {task.status !== 'cancelled' && task.status !== 'completed' && (
            <button
              className={`${styles.taskActionBtn} ${styles.taskActionCancel}`}
              title="Cancel"
              onClick={() => handleStatus('cancelled')}
              disabled={updateTask.isPending}
            >
              <Ban size={11} />
            </button>
          )}
          <button
            className={styles.taskActionBtn}
            title={isExpanded ? 'Collapse' : 'Expand / Edit notes'}
            onClick={() => setIsExpanded((v) => !v)}
            disabled={updateTask.isPending}
          >
            {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          <button
            className={`${styles.taskActionBtn} ${styles.taskActionDelete}`}
            title="Delete"
            onClick={handleDelete}
            disabled={updateTask.isPending || deleteTask.isPending}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={styles.taskExpanded}>
          {updateTask.isPending && (
            <div className={styles.taskActionNotice}>Saving task changes...</div>
          )}
          {actionError && <div className={styles.taskActionError}>{actionError}</div>}
          <textarea
            className={styles.taskNotes}
            placeholder="Add notes or description..."
            defaultValue={(task as any).notes || ''}
            rows={3}
            onBlur={(e) => handleNotesChange(e.target.value)}
          />
          <div className={styles.taskCronFields}>
            <input
              className={styles.taskCronInput}
              defaultValue={task.cronExpression || ''}
              placeholder="Cron schedule (optional), e.g. 0 9 * * 1-5"
              onBlur={(e) => handleCronExpressionChange(e.target.value)}
            />
            <input
              className={styles.taskCronInput}
              defaultValue={task.cronTimezone || ''}
              placeholder="Timezone, e.g. America/New_York"
              onBlur={(e) => handleCronTimezoneChange(e.target.value)}
            />
          </div>
          <div className={styles.taskCronHint}>
            Cron runs recurring tasks. Leave blank for one-time due dates.
          </div>

          <div className={styles.taskScheduleEditor}>
            <label className={styles.taskScheduleField}>
              <span>Start Date</span>
              <input
                type="date"
                value={toDateValue(task.startDate)}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </label>
            <label className={styles.taskScheduleField}>
              <span>Start Time</span>
              <input
                type="time"
                value={task.startTime || ''}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              />
            </label>
            <label className={styles.taskScheduleField}>
              <span>End Date</span>
              <input
                type="date"
                value={effectiveEndDate || ''}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </label>
            <label className={styles.taskScheduleField}>
              <span>End Time</span>
              <input
                type="time"
                value={effectiveEndTime || ''}
                onChange={(e) => handleEndTimeChange(e.target.value)}
              />
            </label>
          </div>

          <details className={styles.taskActionDetails} open>
            <summary className={styles.taskActionSummary}>Task Action Details</summary>
            <div className={styles.taskActionBody}>
              <div className={styles.taskActionGrid}>
                <div>
                  <span className={styles.taskActionLabel}>Status</span>
                  <span className={styles.taskActionValue}>
                    {STATUS_LABELS[task.status] || task.status}
                  </span>
                </div>
                <div>
                  <span className={styles.taskActionLabel}>Priority</span>
                  <span className={styles.taskActionValue}>{task.priority}</span>
                </div>
                <div>
                  <span className={styles.taskActionLabel}>Created</span>
                  <span className={styles.taskActionValue}>
                    {task.createdAt ? new Date(task.createdAt).toLocaleString() : '—'}
                  </span>
                </div>
                <div>
                  <span className={styles.taskActionLabel}>Updated</span>
                  <span className={styles.taskActionValue}>
                    {task.updatedAt ? new Date(task.updatedAt).toLocaleString() : '—'}
                  </span>
                </div>
                <div>
                  <span className={styles.taskActionLabel}>Completed</span>
                  <span className={styles.taskActionValue}>
                    {task.completedAt ? new Date(task.completedAt).toLocaleString() : '—'}
                  </span>
                </div>
              </div>
              {(() => {
                try {
                  const meta = task.metadata ? JSON.parse(task.metadata) : {};
                  const autonomous = meta?.autonomous || {};
                  const performed: string[] = [];

                  if (autonomous.lastRunSummary) {
                    performed.push(String(autonomous.lastRunSummary));
                  }

                  const buckets = [
                    autonomous.history,
                    autonomous.actions,
                    autonomous.steps,
                    autonomous.events,
                    meta.history,
                    meta.actions,
                    meta.steps,
                    meta.events,
                  ];

                  for (const bucket of buckets) {
                    if (!Array.isArray(bucket)) continue;
                    for (const entry of bucket) {
                      if (typeof entry === 'string' && entry.trim()) {
                        performed.push(entry.trim());
                      } else if (entry && typeof entry === 'object') {
                        const label =
                          (entry as any).summary ||
                          (entry as any).message ||
                          (entry as any).action ||
                          (entry as any).name ||
                          '';
                        const status = (entry as any).status ? ` (${(entry as any).status})` : '';
                        const line = `${String(label).trim()}${status}`.trim();
                        if (line) performed.push(line);
                      }
                    }
                  }

                  const uniquePerformed = Array.from(new Set(performed)).slice(0, 12);
                  if (!uniquePerformed.length) {
                    return (
                      <div className={styles.taskActionEmpty}>
                        No performed-action history has been recorded yet for this task.
                      </div>
                    );
                  }

                  return (
                    <div className={styles.taskActionPerformedList}>
                      {uniquePerformed.map((line, index) => (
                        <div
                          key={`${task.id}-performed-${index}`}
                          className={styles.taskActionPerformedItem}
                        >
                          <span className={styles.taskActionPerformedIndex}>{index + 1}.</span>
                          <span>{line}</span>
                        </div>
                      ))}
                    </div>
                  );
                } catch {
                  return (
                    <div className={styles.taskActionEmpty}>
                      No action details available for this task.
                    </div>
                  );
                }
              })()}
            </div>
          </details>

          {(() => {
            try {
              const meta = task.metadata ? JSON.parse(task.metadata) : {};
              const autonomous = meta?.autonomous || {};
              if (!autonomous.lastRunAt) return null;

              return (
                <div
                  style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border)',
                    fontSize: '0.85rem',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: 'var(--muted-foreground)',
                    }}
                  >
                    Execution History
                  </div>
                  <div style={{ display: 'grid', gap: '4px', fontSize: '0.8rem' }}>
                    <div>
                      <strong>Runs:</strong> {autonomous.runCount || 0}
                    </div>
                    <div>
                      <strong>Last Run:</strong> {new Date(autonomous.lastRunAt).toLocaleString()}
                    </div>
                    <div>
                      <strong>Status:</strong>{' '}
                      <span
                        style={{
                          color: autonomous.lastRunStatus === 'success' ? '#22c55e' : '#ef4444',
                        }}
                      >
                        {autonomous.lastRunStatus || '—'}
                      </span>
                    </div>
                    {autonomous.lastModel && (
                      <div>
                        <strong>Model:</strong> {autonomous.lastModel}
                      </div>
                    )}
                    {autonomous.lastRunSummary && (
                      <div style={{ marginTop: '4px' }}>
                        <strong>Summary:</strong>
                        <div
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(0,0,0,0.1)',
                            borderRadius: '4px',
                            marginTop: '2px',
                            maxHeight: '100px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                          }}
                        >
                          {autonomous.lastRunSummary}
                        </div>
                      </div>
                    )}
                    {autonomous.lastRunError && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ color: '#ef4444' }}>Error:</strong>
                        <div
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: '4px',
                            marginTop: '2px',
                            maxHeight: '100px',
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#ef4444',
                          }}
                        >
                          {autonomous.lastRunError}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            } catch {
              return null;
            }
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Add Task Form ────────────────────────────────────────────────────────

function AddTaskForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<'asap' | 'schedule' | 'recurring'>('asap');
  const [priority, setPriority] = useState<'medium' | 'high' | 'low' | 'urgent'>('high');
  const [endDate, setEndDate] = useState('');
  const [cronExpression, setCronExpression] = useState('');
  const [cronTimezone, setCronTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload: any = {
        title: title.trim(),
        priority,
      };

      if (mode === 'asap') {
        // Execute immediately - no dates needed
      } else if (mode === 'schedule' && endDate) {
        // One-time task at specific date
        payload.endDate = endDate;
        payload.dueDate = endDate;
      } else if (mode === 'recurring' && cronExpression) {
        // Recurring task with cron
        payload.cronExpression = cronExpression.trim();
        payload.cronTimezone = cronTimezone.trim() || 'UTC';
      }

      const res = await fetch('/_api/taskingbot/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(payload),
      });
      if (res.ok) {
        qc.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
        onClose();
        toast.success(`Task "${title}" created!`);
      } else {
        toast.error('Failed to create task');
      }
    } catch (err) {
      toast.error('Error creating task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.addTaskForm}>
      <div className={styles.addTaskModeTabs}>
        <button
          onClick={() => setMode('asap')}
          className={`${styles.addTaskModeBtn} ${mode === 'asap' ? styles.addTaskModeBtnActive : ''}`}
        >
          ⚡ Run Now
        </button>
        <button
          onClick={() => setMode('schedule')}
          className={`${styles.addTaskModeBtn} ${mode === 'schedule' ? styles.addTaskModeBtnActive : ''}`}
        >
          📅 Schedule
        </button>
        <button
          onClick={() => setMode('recurring')}
          className={`${styles.addTaskModeBtn} ${mode === 'recurring' ? styles.addTaskModeBtnActive : ''}`}
        >
          🔄 Recurring
        </button>
      </div>

      <input
        className={styles.addTaskInput}
        placeholder="What should TaskingBot do?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleAdd();
          if (e.key === 'Escape') onClose();
        }}
        autoFocus
      />

      {mode === 'asap' && (
        <div className={styles.addTaskRow}>
          <select
            className={styles.addTaskPriority}
            value={priority}
            onChange={(e) => setPriority(e.target.value as any)}
          >
            {['urgent', 'high', 'medium', 'low'].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            className={styles.addTaskSave}
            onClick={handleAdd}
            disabled={!title.trim() || saving}
          >
            {saving ? '...' : '✓ Execute Now'}
          </button>
          <button className={styles.addTaskCancel} onClick={onClose}>
            <X size={13} />
          </button>
        </div>
      )}

      {mode === 'schedule' && (
        <div className={styles.addTaskRow}>
          <select
            className={styles.addTaskPriority}
            value={priority}
            onChange={(e) => setPriority(e.target.value as any)}
          >
            {['urgent', 'high', 'medium', 'low'].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <input
            type="date"
            className={styles.addTaskDate}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="Run on date..."
          />
          <button
            className={styles.addTaskSave}
            onClick={handleAdd}
            disabled={!title.trim() || !endDate || saving}
          >
            {saving ? '...' : '✓ Schedule'}
          </button>
          <button className={styles.addTaskCancel} onClick={onClose}>
            <X size={13} />
          </button>
        </div>
      )}

      {mode === 'recurring' && (
        <div>
          <div className={styles.taskCronPresets}>
            <button onClick={() => setCronExpression('0 * * * *')} title="Run every hour">
              Hourly
            </button>
            <button onClick={() => setCronExpression('0 9 * * *')} title="Run daily at 9 AM">
              Daily
            </button>
            <button onClick={() => setCronExpression('0 9 * * 1')} title="Run every Monday">
              Weekly
            </button>
            <button onClick={() => setCronExpression('0 9 * * 1,4')} title="Mon & Thu">
              2x/week
            </button>
            <button onClick={() => setCronExpression('0 9 1 * *')} title="1st of month">
              Monthly
            </button>
          </div>
          <div className={styles.taskCronFields}>
            <input
              className={styles.taskCronInput}
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              placeholder="Custom cron: 0 9 * * 1-5"
            />
            <input
              className={styles.taskCronInput}
              value={cronTimezone}
              onChange={(e) => setCronTimezone(e.target.value)}
              placeholder="Timezone (e.g. America/New_York)"
            />
          </div>
          <div className={styles.addTaskRow}>
            <select
              className={styles.addTaskPriority}
              value={priority}
              onChange={(e) => setPriority(e.target.value as any)}
            >
              {['urgent', 'high', 'medium', 'low'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button
              className={styles.addTaskSave}
              onClick={handleAdd}
              disabled={!title.trim() || !cronExpression.trim() || saving}
            >
              {saving ? '...' : '✓ Create'}
            </button>
            <button className={styles.addTaskCancel} onClick={onClose}>
              <X size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Scheduled Tasks Panel (replaces Workflows) ─────────────────────────

function ScheduledTasksPanel() {
  const { data: allTasks, isLoading } = useTaskingBotTasks();
  const scheduledTasks = (allTasks || []).filter((t) => t.cronExpression);

  if (isLoading) {
    return <div style={{ padding: 24, color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>Loading scheduled tasks...</div>;
  }

  if (scheduledTasks.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Clock size={32} style={{ margin: '0 auto 12px', color: 'var(--muted-foreground)', opacity: 0.5 }} />
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--foreground)', margin: '0 0 6px' }}>No scheduled tasks yet</p>
        <p style={{ fontSize: '0.8125rem', color: 'var(--muted-foreground)', margin: 0 }}>
          Ask TaskingBot to create a recurring task, e.g. "create a daily task at 8pm to email me lead reports"
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {scheduledTasks.map((task) => (
        <div
          key={task.id}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            border: '1px solid color-mix(in srgb, var(--border) 60%, transparent)',
            background: 'color-mix(in srgb, var(--foreground) 2%, transparent)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {task.title}
              </div>
              {task.description && (
                <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.description}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{
                fontSize: '0.6875rem',
                fontFamily: 'var(--font-family-monospace)',
                padding: '2px 8px',
                borderRadius: 6,
                background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                color: 'var(--primary)',
                fontWeight: 600,
              }}>
                {task.cronExpression}
              </span>
              <span style={{
                fontSize: '0.625rem',
                padding: '2px 6px',
                borderRadius: 6,
                background: task.status === 'completed' ? 'color-mix(in srgb, var(--success, #22c55e) 12%, transparent)' : 'color-mix(in srgb, var(--foreground) 5%, transparent)',
                color: task.status === 'completed' ? 'var(--success, #22c55e)' : 'var(--muted-foreground)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                {task.status}
              </span>
            </div>
          </div>
          {task.cronTimezone && (
            <div style={{ fontSize: '0.6875rem', color: 'var(--muted-foreground)', marginTop: 4 }}>
              Timezone: {task.cronTimezone}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Settings Modal ───────────────────────────────────────────────────────

type SettingsTab = 'channels' | 'integrations' | 'skills' | 'scheduled';

export function TaskingBotSettingsModal({
  onClose,
  initialTab,
}: {
  onClose: () => void;
  initialTab?: SettingsTab;
}) {
  const { data: channels } = useTaskingBotChannels();
  const { data: credData } = useCredentials();
  const { data: customIntegrationsData } = useCustomIntegrations();
  const [tab, setTab] = useState<SettingsTab>(initialTab || 'channels');
  const [settingsSearch, setSettingsSearch] = useState('');

  // Fetch tasks waiting for credentials to highlight needed integrations
  const { data: waitingTasksNeeds } = useQuery({
    queryKey: ['taskingbot-waiting-needs'],
    queryFn: async () => {
      const res = await fetch('/_api/taskingbot/running-tasks');
      if (!res.ok) return [];
      const text = await res.text();
      let tasks: any[] = [];
      try {
        const { parse } = await import('superjson');
        tasks = parse<any[]>(text) || [];
      } catch {
        tasks = JSON.parse(text) || [];
      }
      // Collect all needs from waiting_for_input tasks
      const needs = new Set<string>();
      for (const t of tasks) {
        if (t.status === 'waiting_for_input' && Array.isArray(t.needs)) {
          for (const n of t.needs) needs.add(n.toLowerCase());
        }
      }
      return Array.from(needs);
    },
    refetchInterval: 5000,
  });

  // Map needs to integration keys (clawdhub/openclaw use global server keys, not per-user)
  const neededIntegrationKeys = new Set<string>();
  if (waitingTasksNeeds) {
    for (const need of waitingTasksNeeds) {
      if (need.includes('github')) neededIntegrationKeys.add('github');
      if (need.includes('slack')) neededIntegrationKeys.add('slack');
      if (need.includes('discord')) neededIntegrationKeys.add('discord');
      if (need.includes('notion')) neededIntegrationKeys.add('notion');
      if (need.includes('trello')) neededIntegrationKeys.add('trello');
      if (need.includes('kalshi')) neededIntegrationKeys.add('kalshi');
    }
  }
  const [configuring, setConfiguring] = useState<TaskingBotChannelType | null>(null);
  const [configuringIntegration, setConfiguringIntegration] = useState<string | null>(null);
  const [configuringCustomIntegration, setConfiguringCustomIntegration] = useState<string | null>(
    null,
  );
  const [showCreateCustomIntegration, setShowCreateCustomIntegration] = useState(false);

  const channelMap = new Map<string, ChannelConfig>();
  channels?.forEach((ch) => channelMap.set(ch.channelType, ch));
  const customIntegrations = customIntegrationsData?.integrations || [];
  const customIntegrationMetaByName: Record<string, IntegrationMeta> = Object.fromEntries(
    customIntegrations.map((ci) => [
      ci.name,
      {
        label: ci.label,
        icon: Link,
        description: ci.description || 'Custom integration',
        fields: ci.fields.map((field) => ({
          key: field.key,
          label: field.label,
          type: field.type,
          placeholder: field.placeholder || '',
          required: field.required,
        })),
      } as IntegrationMeta,
    ]),
  );

  const normalizedSettingsSearch = settingsSearch.trim().toLowerCase();
  const matchesSettingsSearch = (...parts: Array<string | undefined>) =>
    !normalizedSettingsSearch ||
    parts.some((part) => (part || '').toLowerCase().includes(normalizedSettingsSearch));

  const visibleChannelTypes = ALL_CHANNEL_TYPES.filter((type) => {
    const meta = CHANNEL_META[type];
    return matchesSettingsSearch(type, meta.label);
  });

  const visibleIntegrations = ALL_INTEGRATIONS.filter((key) => {
    const meta = INTEGRATION_META[key];
    return matchesSettingsSearch(key, meta.label, meta.description);
  }).sort((a, b) => {
    // Sort needed-but-disconnected integrations first
    const aNeeded =
      neededIntegrationKeys.has(a) &&
      !((credData?.credentials || credData?.integrations)?.[a] && Object.keys((credData?.credentials || credData?.integrations || {})[a]).length > 0);
    const bNeeded =
      neededIntegrationKeys.has(b) &&
      !((credData?.credentials || credData?.integrations)?.[b] && Object.keys((credData?.credentials || credData?.integrations || {})[b]).length > 0);
    if (aNeeded && !bNeeded) return -1;
    if (!aNeeded && bNeeded) return 1;
    return 0;
  });
  const visibleCustomIntegrations = customIntegrations.filter((ci) =>
    matchesSettingsSearch(ci.name, ci.label, ci.description || '', ...ci.fields.map((f) => f.key)),
  );

  const visibleSkillCategories = SKILL_CATEGORIES.map((category) => ({
    ...category,
    skills: category.skills.filter(
      (skill) =>
        !normalizedSettingsSearch || skill.toLowerCase().includes(normalizedSettingsSearch),
    ),
  })).filter(
    (category) =>
      !normalizedSettingsSearch ||
      category.name.toLowerCase().includes(normalizedSettingsSearch) ||
      category.skills.length > 0,
  );

  const quickAddLabel =
    tab === 'integrations'
      ? 'Create custom integration'
      : tab === 'channels'
        ? 'Add channel'
        : tab === 'skills'
          ? 'Add skill'
          : 'Add workflow';

  const searchPlaceholder =
    tab === 'integrations'
      ? 'Search integrations...'
      : tab === 'channels'
        ? 'Search channels...'
        : tab === 'skills'
          ? 'Search skills...'
          : 'Search workflows...';

  const handleQuickAdd = () => {
    if (tab === 'integrations') {
      setSettingsSearch('');
      setConfiguring(null);
      setConfiguringIntegration(null);
      setConfiguringCustomIntegration(null);
      setShowCreateCustomIntegration(true);
      return;
    }

    if (tab === 'channels') {
      const firstDisconnected =
        visibleChannelTypes.find((type) => {
          if (type === 'web') return false;
          const saved = channelMap.get(type);
          return (saved?.status || 'disconnected') !== 'connected';
        }) ??
        visibleChannelTypes.find((type) => type !== 'web') ??
        visibleChannelTypes[0];
      if (!firstDisconnected) {
        toast.info('No channels match your search');
        return;
      }
      setConfiguringIntegration(null);
      setConfiguringCustomIntegration(null);
      setShowCreateCustomIntegration(false);
      setConfiguring(firstDisconnected);
      return;
    }

    if (tab === 'skills') {
      toast.info('Use TaskingBot chat and run /skills to install or enable skills');
      return;
    }

    toast.info('Use TaskingBot chat to create scheduled tasks with /create-task or natural language');
  };

  return (
    <div className={styles.settingsOverlay} onClick={onClose}>
      <div className={styles.settingsModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.settingsHeader}>
          <div className={styles.settingsTabs}>
            {(['channels', 'integrations', 'skills', 'scheduled'] as SettingsTab[]).map((t) => (
              <button
                key={t}
                className={`${styles.settingsTab} ${tab === t ? styles.settingsTabActive : ''}`}
                onClick={() => {
                  setTab(t);
                  if (t !== 'integrations') {
                    setConfiguringIntegration(null);
                    setConfiguringCustomIntegration(null);
                    setShowCreateCustomIntegration(false);
                  }
                  if (t !== 'channels') {
                    setConfiguring(null);
                  }
                }}
              >
                {t === 'scheduled' ? 'Scheduled Tasks' : t.charAt(0).toUpperCase() + t.slice(1)}
                {t === 'integrations' && neededIntegrationKeys.size > 0 && (
                  <AlertTriangle
                    size={12}
                    style={{ marginLeft: 4, color: 'var(--warning, #f59e0b)', display: 'inline' }}
                  />
                )}
              </button>
            ))}
          </div>
          <div className={styles.settingsHeaderActions}>
            <label className={styles.settingsSearchWrap} aria-label={searchPlaceholder}>
              <Search size={14} className={styles.settingsSearchIcon} />
              <input
                type="text"
                value={settingsSearch}
                onChange={(e) => setSettingsSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className={styles.settingsHeaderSearch}
              />
            </label>
            <button className={styles.settingsPlus} onClick={handleQuickAdd} title={quickAddLabel}>
              <Plus size={16} />
            </button>
            <button className={styles.settingsClose} onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>

        <div className={styles.settingsBody}>
          {tab === 'channels' && (
            <div className={styles.settingsList}>
              {visibleChannelTypes.map((type) => {
                const Icon = CHANNEL_ICONS[type];
                const meta = CHANNEL_META[type];
                const saved = channelMap.get(type);
                const status = type === 'web' ? 'connected' : saved?.status || 'disconnected';
                return (
                  <Fragment key={type}>
                    <div
                      className={`${styles.item} ${styles.itemClickable}`}
                      onClick={() => setConfiguring(configuring === type ? null : type)}
                    >
                      <div className={styles.itemIcon}>
                        <Icon size={14} />
                      </div>
                      <div className={styles.itemInfo}>
                        <div className={styles.itemName}>{meta.label}</div>
                      </div>
                      {status === 'connected' ? (
                        <span className={`${styles.itemBadge} ${styles.badgeConnected}`}>
                          Connected
                        </span>
                      ) : status === 'error' ? (
                        <span className={`${styles.itemBadge} ${styles.badgeError}`}>Error</span>
                      ) : (
                        <button className={styles.configBtn} title="Configure">
                          <Settings size={12} />
                        </button>
                      )}
                    </div>
                    {configuring === type && (
                      <ChannelConfigForm
                        channelType={type}
                        existing={saved}
                        onClose={() => setConfiguring(null)}
                      />
                    )}
                  </Fragment>
                );
              })}
              {visibleChannelTypes.length === 0 && (
                <div className={styles.settingsEmpty}>No channels match your search.</div>
              )}
            </div>
          )}

          {tab === 'integrations' && (
            <SimpleCredentialsPanel credData={credData} />
          )}

          {tab === 'skills' && (
            <div className={styles.settingsSkills}>
              {visibleSkillCategories.map((category) => (
                <div key={category.name} className={styles.skillCategory}>
                  <div className={styles.skillCategoryTitle}>{category.name}</div>
                  <div className={styles.skillList}>
                    {category.skills.map((skill) => (
                      <span key={skill} className={styles.skillTag}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {visibleSkillCategories.length === 0 && (
                <div className={styles.settingsEmpty}>No skills match your search.</div>
              )}
            </div>
          )}

          {tab === 'scheduled' && <ScheduledTasksPanel />}
        </div>
      </div>
    </div>
  );
}

// ─── Main TaskingBotDashboard ─────────────────────────────────────────────

type TaskFilter = 'all' | 'today' | 'open' | 'in_progress' | 'completed' | 'waiting_for_input';

export function TaskingBotDashboard({ projectId }: { projectId?: string | null }) {
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all');
  const [showAddTask, setShowAddTask] = useState(true); // Always show form by default

  const filters: Record<string, string> = {};
  if (taskFilter !== 'all') filters.status = taskFilter;
  if (projectId) filters.projectId = projectId;
  const { data: tasks, isLoading: isTasksLoading } = useTaskingBotTasks(
    Object.keys(filters).length > 0 ? filters : undefined,
  );

  const visibleTasks =
    tasks?.filter(
      (task) => !task.parentTaskId || !tasks.some((candidate) => candidate.id === task.parentTaskId),
    ) || [];

  const openCount =
    visibleTasks.filter((t) => t.status === 'open' || t.status === 'in_progress').length ?? 0;

  // Group tasks by end/due date for schedule view
  const today = new Date().toISOString().slice(0, 10);
  const getTaskEndDate = (task: TaskingBotTask) => task.endDate || task.dueDate;
  const overdue =
    visibleTasks.filter((t) => {
      const endDate = getTaskEndDate(t);
      return Boolean(
        endDate && endDate < today && t.status !== 'completed' && t.status !== 'cancelled',
      );
    }) || [];
  const todayTasks = visibleTasks.filter((t) => {
    const endDate = getTaskEndDate(t);
    if (endDate === today) return true;
    // Include tasks created today that have no end/due date
    if (!endDate && t.createdAt && t.createdAt.slice(0, 10) === today) return true;
    // Include cron-scheduled tasks (they run daily/regularly)
    if (!endDate && t.cronExpression && t.status !== 'completed' && t.status !== 'cancelled') return true;
    return false;
  }) || [];
  const upcoming =
    visibleTasks.filter((t) => {
      const endDate = getTaskEndDate(t);
      return Boolean(
        endDate && endDate > today && t.status !== 'completed' && t.status !== 'cancelled',
      );
    }) || [];
  const noDue =
    visibleTasks.filter(
      (t) => !getTaskEndDate(t) && t.status !== 'completed' && t.status !== 'cancelled',
    ) || [];

  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div className={styles.dashboardTitle}>
          <ListTodo size={16} />
          Tasks
          {openCount > 0 && <span className={styles.taskCount}>{openCount}</span>}
        </div>
        <button className={styles.addTaskBtn} onClick={() => setShowAddTask(true)} title="Add task">
          <Plus size={14} />
          Add
        </button>
      </div>

      {/* Add Task Form - Always visible inline */}
      <div
        style={{
          padding: '12px',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}
      >
        {showAddTask ? (
          <AddTaskForm onClose={() => setShowAddTask(false)} />
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'center' }}>
            Click "Add" button above to create a new task
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className={styles.taskFilterRow}>
        {(['all', 'today', 'open', 'in_progress', 'completed'] as TaskFilter[]).map((f) => (
          <button
            key={f}
            className={`${styles.taskFilterBtn} ${taskFilter === f ? styles.active : ''}`}
            onClick={() => setTaskFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'today' ? 'Today' : f === 'in_progress' ? 'Active' : f === 'open' ? 'Open' : 'Done'}
          </button>
        ))}
      </div>

      {/* Task List */}
      {isTasksLoading ? (
        <div className={styles.emptyTasks}>Loading tasks...</div>
      ) : !visibleTasks || visibleTasks.length === 0 ? (
        <div className={styles.emptyTasks}>
          <ListTodo size={20} style={{ opacity: 0.4 }} />
          <div>No tasks yet. Tell TaskingBot to create one or click Add.</div>
        </div>
      ) : taskFilter === 'today' ? (
        <div className={styles.taskList}>
          {todayTasks.length > 0 ? (
            todayTasks.map((task) => <TaskRow key={task.id} task={task} />)
          ) : (
            <div className={styles.emptyTasks}>
              <div>No tasks scheduled for today.</div>
            </div>
          )}
        </div>
      ) : taskFilter !== 'all' ? (
        <div className={styles.taskList}>
          {visibleTasks.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </div>
      ) : (
        <div className={styles.taskList}>
          {overdue.length > 0 && (
            <div className={styles.scheduleGroup}>
              <div className={styles.scheduleGroupLabel} style={{ color: 'var(--error, #ef4444)' }}>
                Overdue ({overdue.length})
              </div>
              {overdue.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
          {todayTasks.length > 0 && (
            <div className={styles.scheduleGroup}>
              <div
                className={styles.scheduleGroupLabel}
                style={{ cursor: 'pointer' }}
                onClick={() => setTaskFilter('today')}
              >
                Today ({todayTasks.length})
              </div>
              {todayTasks.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
          {upcoming.length > 0 && (
            <div className={styles.scheduleGroup}>
              <div className={styles.scheduleGroupLabel}>Upcoming</div>
              {upcoming.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
          {noDue.length > 0 && (
            <div className={styles.scheduleGroup}>
              <div className={styles.scheduleGroupLabel}>No date</div>
              {noDue.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
          {/* Completed (collapsed by default) */}
          {visibleTasks.filter((t) => t.status === 'completed' || t.status === 'cancelled').length > 0 && (
            <details className={styles.doneGroup}>
              <summary className={styles.scheduleGroupLabel}>
                Done / Cancelled (
                {visibleTasks.filter((t) => t.status === 'completed' || t.status === 'cancelled').length})
              </summary>
              {visibleTasks
                .filter((t) => t.status === 'completed' || t.status === 'cancelled')
                .map((t) => (
                  <TaskRow key={t.id} task={t} />
                ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
}
